
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, Loader2 } from "lucide-react";
import { useUserSettings } from "@/hooks/useUserSettings";
import { useProductDetailsSettings } from "@/hooks/useProductDetailsSettings";
import { useAggregatedData } from "./aggregated-download/useAggregatedData";
import { useExcelExporter } from "./aggregated-download/excelExporter";
import { useCSVExporter } from "./aggregated-download/csvExporter";
import { transformDataForExport } from "./aggregated-download/dataTransformer";
import { AggregatedDownloadButtonProps } from "./aggregated-download/types";

export const AggregatedDownloadButton = ({ 
  collectionId, 
  collectionName 
}: AggregatedDownloadButtonProps) => {
  const [isExporting, setIsExporting] = useState(false);
  const { settings } = useUserSettings();
  const { data: productDetailsSettings } = useProductDetailsSettings();
  const { fetchAggregatedData } = useAggregatedData();
  const { exportToExcel } = useExcelExporter();
  const { exportToCSV } = useCSVExporter();

  const handleAggregatedExport = async () => {
    try {
      setIsExporting(true);

      const result = await fetchAggregatedData(collectionId);
      if (!result) return;

      const { aggregatedData, languages } = result;
      const exportData = transformDataForExport(
        aggregatedData, 
        languages, 
        productDetailsSettings?.showProductDetails || false
      );
      const filename = `${collectionName}-aggregated`;
      const exportFormat = String(settings?.export_format || 'csv');

      if (exportFormat === 'excel') {
        await exportToExcel(exportData, filename, languages, productDetailsSettings?.showProductDetails || false);
      } else {
        await exportToCSV(exportData, filename);
      }
    } catch (error) {
      console.error('Aggregated export error:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const exportFormat = String(settings?.export_format || 'csv');

  return (
    <Button
      onClick={handleAggregatedExport}
      disabled={isExporting}
      className="bg-green-600 hover:bg-green-700 text-white border-green-600 dark:bg-green-600 dark:hover:bg-green-700 dark:text-white"
      variant="default"
    >
      {isExporting ? (
        <>
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          Exporting...
        </>
      ) : (
        <>
          <Download className="h-4 w-4 mr-2" />
          Download {exportFormat === 'excel' ? 'Excel' : 'CSV'}
        </>
      )}
    </Button>
  );
};
